import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';

const API_BASE_URL = getApiUrl();

interface MessageQueueItem {
  id: string;
  contact_id: string;
  whatsapp_instance_id: string;
  auto_message_id: string;
  message_content: string;
  message_type: string;
  status: 'pending' | 'sending' | 'sent' | 'failed';
  priority: number;
  scheduled_at: string;
  sent_at?: string;
  failed_at?: string;
  error_message?: string;
  retry_count: number;
  max_retries: number;
  created_at: string;
  updated_at: string;
  contacts: {
    name: string;
    custom_fields: {
      whatsapp_id?: string;
    };
  };
  whatsapp_instances: {
    name: string;
    phone_number?: string;
  };
  whatsapp_auto_messages: {
    name: string;
  };
}

const MessageQueue: React.FC = () => {
  const [queueItems, setQueueItems] = useState<MessageQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MessageQueueItem | null>(null);
  const [processing, setProcessing] = useState(false);

  // Buscar itens da fila
  const fetchQueueItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API_BASE_URL}/whatsapp-auto/queue`, {
        timeout: 30000
      });

      if (response.data.success) {
        setQueueItems(response.data.data || []);
      } else {
        setError('Erro ao carregar fila de mensagens');
      }
    } catch (err) {
      console.error('Erro ao buscar fila:', err);
      setError('Erro ao carregar fila de mensagens');
    } finally {
      setLoading(false);
    }
  }, []);

  // Processar fila manualmente
  const processQueue = useCallback(async () => {
    try {
      setProcessing(true);
      const response = await axios.post(`${API_BASE_URL}/whatsapp-auto/queue/process`);
      
      if (response.data.success) {
        await fetchQueueItems(); // Recarregar lista
        console.log(`✅ ${response.data.data.processed_count} mensagens processadas`);
      }
    } catch (err) {
      console.error('Erro ao processar fila:', err);
    } finally {
      setProcessing(false);
    }
  }, [fetchQueueItems]);

  // Apagar item da fila
  const deleteQueueItem = useCallback(async (itemId: string) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/whatsapp-auto/queue/${itemId}`);
      
      if (response.data.success) {
        await fetchQueueItems(); // Recarregar lista
        console.log('✅ Item removido da fila');
      }
    } catch (err) {
      console.error('Erro ao apagar item:', err);
    }
  }, [fetchQueueItems]);

  // Confirmar exclusão
  const handleDeleteClick = (item: MessageQueueItem) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedItem) {
      await deleteQueueItem(selectedItem.id);
      setDeleteDialogOpen(false);
      setSelectedItem(null);
    }
  };

  // Formatar status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'sending': return 'info';
      case 'sent': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'sending': return 'Enviando';
      case 'sent': return 'Enviada';
      case 'failed': return 'Falhou';
      default: return status;
    }
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  // Preview da mensagem
  const getMessagePreview = (content: string) => {
    return content.length > 100 ? `${content.substring(0, 100)}...` : content;
  };

  useEffect(() => {
    fetchQueueItems();
    
    // Auto-refresh a cada 30 segundos
    const interval = setInterval(fetchQueueItems, 30000);
    return () => clearInterval(interval);
  }, [fetchQueueItems]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Fila de Mensagens WhatsApp
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchQueueItems}
            disabled={loading}
          >
            Atualizar
          </Button>
          
          <Button
            variant="contained"
            startIcon={processing ? <CircularProgress size={20} /> : <PlayIcon />}
            onClick={processQueue}
            disabled={processing}
            color="primary"
          >
            {processing ? 'Processando...' : 'Processar Fila'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contato</TableCell>
                  <TableCell>Mensagem</TableCell>
                  <TableCell>Instância</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Agendado</TableCell>
                  <TableCell>Tentativas</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : queueItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Nenhuma mensagem na fila
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  queueItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {item.contacts.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.contacts.custom_fields.whatsapp_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" noWrap>
                            {getMessagePreview(item.message_content)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.whatsapp_auto_messages.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {item.whatsapp_instances.name}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Chip
                          label={getStatusText(item.status)}
                          color={getStatusColor(item.status) as any}
                          size="small"
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(item.scheduled_at)}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {item.retry_count}/{item.max_retries}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {item.status === 'pending' && (
                            <Tooltip title="Remover da fila">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(item)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {item.error_message && (
                            <Tooltip title={item.error_message}>
                              <IconButton size="small" color="warning">
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover esta mensagem da fila?
          </Typography>
          {selectedItem && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Contato:</strong> {selectedItem.contacts.name}
              </Typography>
              <Typography variant="body2">
                <strong>Mensagem:</strong> {getMessagePreview(selectedItem.message_content)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageQueue;
